/**
 * Box I (docs/superpowers/specs/2026-10-28-native-budget-mmm-design.md §5.8), drawn before a usable model version: per
 * channel, the scope's live 28-day spend and its Cost per Result in the main objective, against the 28 days before.
 * Plain facts, not MMM. Cost per Result reads D1's Result cut of each window (T10j), so it equals /overview's and late
 * conversions never lean its Δ red; spend, the main objective and stopped read the whole windows. Pure.
 * The caller passes only the scope currency's rows (spec §5.9); a row of unknown currency is skipped anyway (T10f).
 *
 * @module services/budget/interim
 */
import type { DateRange } from '@/services/looker/lookerDateRange';
import { asTargetObjective, resultFiguresOf } from '@/services/overview/objectiveResults';
import { groupRows, totalsOf } from '@/services/overview/overviewMath';
import type { FactPair, FactRow } from '@/services/overview/overviewTypes';
import { rowsIn } from '@/services/overview/overviewWindows';
import {
  DEFAULT_AWARENESS, metricFor, type AwarenessMetric, type ResultMappingInfo, type ResultMetric,
} from '@/services/targets/resultMetric';
import { nullLast } from './scopeRows';

/** OverviewWindows' cut and cutPrevious: inside the read windows; null = no Result window, so no Cost per Result. */
type ResultCut = { current: DateRange | null; previous: DateRange | null };

export interface InterimRow {
  channel: string;
  spend: number | null;
  spendPrev: number | null;
  /** By current spend; by the previous window's when the channel spent nothing now (T10e). */
  mainObjective: string | null;
  /** What cpr and cprPrev are per (rules-04 E3); null where resultFiguresOf gives no Result (T12f). */
  metric: ResultMetric | null;
  cpr: number | null;
  cprPrev: number | null;
  /** Spent in the previous window and not in the current one. */
  stopped: boolean;
}

/** D3's read of the mapping (spec §3.1): a failed read blanks Awareness only, the one objective whose metric it names. */
export const awarenessFor = (mapping: ResultMappingInfo, objective: string): AwarenessMetric | null =>
  mapping.awareness ?? (objective === 'awareness' ? null : DEFAULT_AWARENESS);

/** The objective with the most spend, ties by name; null when nothing spent. */
export const mainObjectiveOf = (rows: readonly FactRow[]): string | null =>
  groupRows(rows, (r) => r.objective)
    .map(([objective, own]) => ({ objective, spend: totalsOf(own).spend ?? 0 }))
    .filter((o) => o.spend > 0)
    .sort((a, b) => b.spend - a.spend || a.objective.localeCompare(b.objective))[0]?.objective ?? null;

/** resultFiguresOf's Cost per Result in one objective (null without one), the mapping read as D3 reads it. */
export const costPerResultOf = (rows: readonly FactRow[], objective: string | null, mapping: ResultMappingInfo): number | null =>
  (objective === null ? null : resultFiguresOf(rows, objective, awarenessFor(mapping, objective)).costPerResult);

/** The metric costPerResultOf counts, as resultFiguresOf picks it: null outside the three objectives or without an Awareness definition. */
export const resultMetricOf = (objective: string | null, mapping: ResultMappingInfo): ResultMetric | null => {
  const o = objective === null ? null : asTargetObjective(objective);
  const awareness = o === null ? null : awarenessFor(mapping, o);
  return o === null || awareness === null ? null : metricFor(o, awareness);
};

const interimRowOf = (
  channel: string, now: readonly FactRow[], before: readonly FactRow[], mapping: ResultMappingInfo, cut: ResultCut,
): InterimRow => {
  const spend = totalsOf(now).spend;
  const spendPrev = totalsOf(before).spend;
  const mainObjective = mainObjectiveOf(now) ?? mainObjectiveOf(before);
  return {
    channel, spend, spendPrev, mainObjective, metric: resultMetricOf(mainObjective, mapping),
    cpr: costPerResultOf(rowsIn(now, cut.current), mainObjective, mapping),
    cprPrev: costPerResultOf(rowsIn(before, cut.previous), mainObjective, mapping),
    // "Has it spent" only: null spend is no spend here, and it is never displayed as 0.
    stopped: (spendPrev ?? 0) > 0 && (spend ?? 0) <= 0,
  };
};

/** A row with no account is outside every scope (plan R2); scopeIds null = nothing confirmed, so every account (T10b). */
const scoped = (rows: readonly FactRow[], scopeIds: readonly string[] | null): FactRow[] =>
  rows.filter((r) => r.currency !== null && r.accountId !== null && (scopeIds === null || scopeIds.includes(r.accountId)));

/** One row per channel that spent in either window: spend, highest first and nulls last, then the channel. */
export const interimRowsOf = (
  pair: FactPair, scopeIds: readonly string[] | null, mapping: ResultMappingInfo, cut: ResultCut,
): InterimRow[] => {
  const current = scoped(pair.current, scopeIds);
  const previous = scoped(pair.previous, scopeIds);
  return [...new Set([...current, ...previous].map((r) => r.platform))]
    .map((c) => interimRowOf(c, current.filter((r) => r.platform === c), previous.filter((r) => r.platform === c), mapping, cut))
    // As the prototype draws it: a channel that spent in neither window has nothing to compare.
    .filter((r) => (r.spend ?? 0) > 0 || (r.spendPrev ?? 0) > 0)
    .sort((a, b) => nullLast(a.spend, b.spend) || (b.spend ?? 0) - (a.spend ?? 0) || a.channel.localeCompare(b.channel));
};
